import { createElement, forwardRef, MouseEvent } from 'react';

import { Modal, ModalContent, Typography } from '@scaleaq/scaleui';
import styled from 'styled-components';

import { Z_INDEX } from '@/constants';
import { ModalProps } from '@/types.ts';
import { colors, responsive } from '@/utils';

/**
 * Modal component with default z-index set to overlay.panel
 * This ensures all modals have consistent z-index without needing to set it manually.
 * The zIndex can still be overridden if needed for specific cases.
 */
export const ModalWithDefaultZIndex = forwardRef<HTMLDialogElement, ModalProps>((props, ref) => {
  const { zIndex = Z_INDEX.overlay.panel, onDoubleClick, ...restProps } = props;

  // Stop double-clicks inside any modal from bubbling (via the React tree, even when the dialog is
  // portaled) to ancestors such as the landing-page grid cell, whose double-click handler opens the
  // widget debug modal. Any consumer-provided handler still runs.
  const handleDoubleClick = (event: MouseEvent<HTMLDialogElement>) => {
    event.stopPropagation();
    onDoubleClick?.(event);
  };

  return createElement(Modal, { ref, zIndex, onDoubleClick: handleDoubleClick, ...restProps });
});

ModalWithDefaultZIndex.displayName = 'ModalWithDefaultZIndex';

export const StyledModal = styled(ModalWithDefaultZIndex)<{
  $minHeight?: string;
  $maxWidth?: string;
  $width?: string;
}>`
  width: ${({ $width }) => $width || '50vw'};
  min-height: ${({ $minHeight }) => $minHeight || '80vh'};
  max-width: ${({ $maxWidth }) => $maxWidth || 'none'};
`;

export const StyledModalContent = styled(ModalContent)<{ $minWidth?: string }>`
  width: 100%;
  min-width: ${({ $minWidth }) => $minWidth ?? '30rem'};
  min-height: 26rem;
  overflow-y: auto;
`;

/** Matches ScaleUI Modal default `maxHeight`; pass as `height` so grid `1fr` content scrolls internally. */
const FIXED_HEIGHT_MODAL_HEIGHT = 'min(50rem, calc(100vh - 8rem))';

/** Fixed viewport height; overrides {@link StyledModal} default `min-height: 80vh`. */
export const FixedHeightStyledModal = styled(StyledModal).attrs({
  height: FIXED_HEIGHT_MODAL_HEIGHT,
  $minHeight: '0',
})``;

/** Fills modal content grid row; pair with an inner scroll container for overflow. */
export const ModalGridContent = styled.div`
  grid-area: content;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  position: relative;
`;

export const MODAL_CONTENT_PADDING = responsive.spacing['2xlarge'];

/** Modal content grid cell that scrolls as one block (no inner fixed chrome). */
export const ModalGridScrollContent = styled.div`
  grid-area: content;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  padding: ${MODAL_CONTENT_PADDING};
  position: relative;
`;

export const EllipsisTypography = styled(Typography)<{ $titleZIndex?: number }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-all;
  z-index: ${({ $titleZIndex }) => $titleZIndex || 1};
`;

export const GroupBorder = styled.div`
  border: 1px solid ${colors.stroke.neutral['50']};
  width: 100%;
  padding: ${responsive.spacing['3xlarge']};
  border-radius: ${responsive.radius.xlarge};
  background-color: ${colors.surface.sunken};
`;
